"use client";

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Alert } from "@/components/ui/alert";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { Ticket } from "@/lib/types/database";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Clock, AlertCircle, CheckCircle } from "lucide-react";
import Link from "next/link";

interface TicketPopoverProps {
  ticketId: string;
  children: React.ReactNode;
}

// Ticket already has contacts via GraphQL relation
// This type is for internal use where we need explicit contact array
type TicketWithContact = Ticket & {
  _contactData?: Array<{
    id: string;
    name: string;
    company_name?: string;
    email?: string;
  }>;
};

const fetchTicketById = async (ticketId: string): Promise<TicketWithContact | null> => {
  const supabase = getBrowserSupabaseClient();
  const { data, error } = await supabase
    .from("tickets")
    .select(
      `
      *,
      contacts!inner(
        id,
        name,
        company_name,
        email
      )
    `
    )
    .eq("id", ticketId)
    .single();

  if (error && error.code !== "PGRST116") {
    // PGRST116 means no rows found, which is not an error in this case.
    throw error;
  }

  return data;
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case "open":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    case "in_progress":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
    case "resolved":
      return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
    case "closed":
      return "bg-card-100 text-gray-800 dark:text-gray-200";
    default:
      return "bg-card-100 text-gray-800 dark:text-gray-200";
  }
};

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case "critical":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
    case "urgent":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
    case "high":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
    case "medium":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    case "low":
      return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
    default:
      return "bg-card-100 text-gray-800 dark:text-gray-200";
  }
};

export const TicketPopover = ({ ticketId, children }: TicketPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    data: ticket,
    isLoading,
    error,
  } = useQuery<TicketWithContact | null, Error>({
    queryKey: ["ticket", ticketId],
    queryFn: () => fetchTicketById(ticketId),
    enabled: !!ticketId,
  });

  const contact = ticket?._contactData?.[0];

  const popoverContent = (
    <div className="grid gap-4">
      <div className="space-y-2">
        {isLoading && <LoadingSpinner />}
        {error && <Alert>Error: {error.message}</Alert>}
        {ticket ? (
          <div className="space-y-3">
            {/* Ticket Header */}
            <div className="space-y-1">
              <h4 className="font-medium leading-none text-card-foreground">
                WTC-{String(ticket.ticket_number).padStart(6, "0")}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{ticket.title}</p>
            </div>

            {/* Status and Priority Badges */}
            <div className="flex gap-2 flex-wrap">
              {ticket.status && (
                <Badge className={`text-xs ${getStatusColor(ticket.status)}`}>
                  {ticket.status.replace("_", " ").toUpperCase()}
                </Badge>
              )}
              {ticket.priority && (
                <Badge className={`text-xs ${getPriorityColor(ticket.priority)}`}>
                  {ticket.priority.toUpperCase()}
                </Badge>
              )}
            </div>

            {/* Contact Info */}
            {contact && (
              <div className="bg-card-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">CLIENT</p>
                <p className="text-sm font-medium text-card-foreground">
                  {contact.name || "Unknown"}
                </p>
                {contact.company_name && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">{contact.company_name}</p>
                )}
              </div>
            )}

            {/* Time Tracking Info */}
            {(ticket.estimated_hours || ticket.actual_hours) && (
              <div className="bg-blue-50 p-3 rounded-lg dark:bg-blue-950/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">TIME TRACKING</span>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  {ticket.estimated_hours && (
                    <p className="text-xs text-gray-700 dark:text-gray-300">
                      Estimated: <span className="font-medium">{ticket.estimated_hours}h</span>
                    </p>
                  )}
                  {ticket.actual_hours && ticket.actual_hours > 0 && (
                    <p className="text-xs text-gray-700 dark:text-gray-300">
                      Actual: <span className="font-medium">{ticket.actual_hours}h</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Due Date */}
            {ticket.due_date && (
              <div className="bg-amber-50 p-3 rounded-lg dark:bg-amber-950/40">
                <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">DUE DATE</p>
                <p className="text-sm font-medium text-card-foreground">
                  {new Date(ticket.due_date).toLocaleDateString()}
                </p>
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 gap-2">
              <Link
                href={`/tickets/${ticket.id}`}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <ExternalLink className="h-3 w-3" />
                  View Ticket Details
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-600 dark:text-gray-400">Ticket not found</div>
        )}
      </div>
    </div>
  );

  return (
    <ResponsivePopover
      content={popoverContent}
      title="Ticket Details"
      className="w-80"
      align="center"
      popoverSide="bottom"
      drawerSide="bottom"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      {children}
    </ResponsivePopover>
  );
};
